import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Search, Users, Shield, TrendingUp, UserPlus, GitBranch } from 'lucide-react';
import { followerAutomationTemplates, type AutomationTemplate } from '@/data/automationTemplates';

interface AutomationTemplatesProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: AutomationTemplate, name: string) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  Users: <Users className="w-5 h-5" />,
  Shield: <Shield className="w-5 h-5" />,
  TrendingUp: <TrendingUp className="w-5 h-5" />,
  UserPlus: <UserPlus className="w-5 h-5" />,
  GitBranch: <GitBranch className="w-5 h-5" />
};

export function AutomationTemplates({ isOpen, onClose, onSelectTemplate }: AutomationTemplatesProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<AutomationTemplate | null>(null);
  const [automationName, setAutomationName] = useState('');

  const filteredTemplates = followerAutomationTemplates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectTemplate = (template: AutomationTemplate) => {
    setSelectedTemplate(template);
    setAutomationName(template.name);
  };

  const handleCreateFromTemplate = () => {
    if (selectedTemplate && automationName) {
      onSelectTemplate(selectedTemplate, automationName);
      handleClose();
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedTemplate(null);
    setAutomationName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Automation Templates</DialogTitle>
          <DialogDescription>
            Start with a pre-configured template to quickly set up common automation scenarios
          </DialogDescription>
        </DialogHeader>

        {!selectedTemplate ? (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto flex-1 py-4">
              {filteredTemplates.map((template) => (
                <Card
                  key={template.id}
                  className="cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 rounded-lg">
                          {iconMap[template.icon] || <Users className="w-5 h-5" />}
                        </div>
                        <div>
                          <CardTitle className="text-base">{template.name}</CardTitle>
                          <Badge variant="secondary" className="mt-1">
                            {template.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{template.description}</CardDescription>
                    <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{template.steps.filter(s => s.type === 'trigger').length} trigger</span>
                      <span>•</span>
                      <span>{template.steps.filter(s => s.type === 'action').length} action(s)</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredTemplates.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No templates found matching your search
              </div>
            )}
          </>
        ) : (
          <div className="space-y-4 py-4">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    {iconMap[selectedTemplate.icon] || <Users className="w-5 h-5" />}
                  </div>
                  <div>
                    <CardTitle>{selectedTemplate.name}</CardTitle>
                    <CardDescription className="mt-1">{selectedTemplate.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm font-medium">Automation Flow:</div>
                  {selectedTemplate.steps.map((step, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Badge variant={step.type === 'trigger' ? 'default' : 'secondary'}>
                        {step.type === 'trigger' ? 'Trigger' : 'Action'}
                      </Badge>
                      <span className="text-muted-foreground">
                        {step.triggerType || step.actionType}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label htmlFor="automation-name">Automation Name</Label>
              <Input
                id="automation-name"
                placeholder="Enter a name for your automation"
                value={automationName}
                onChange={(e) => setAutomationName(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                You can customize the automation after creating it from this template
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
                Back
              </Button>
              <Button onClick={handleCreateFromTemplate} disabled={!automationName}>
                Create Automation
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}